/**
 * Content for public-facing pages (landing, how it works, FAQ, cards).
 *
 * Messaging strategy:
 * - Lead with the pain point: forgetting birthdays, anniversaries, milestones.
 * - Position Scheduled Smiles as the one-stop shop for recurring gifting
 *   (free e-cards, mailed greeting cards, flowers) with Auto Send + AI personalization.
 * - Hero headline rotates the occasion word, not the full sentence:
 *   "Never forget another [birthday / anniversary / milestone]."
 * - Hero copy is three beats: benefit line, category line, then highlight chips
 *   (keeps the first viewport compact and scannable).
 * - FAQ uses human-sounding questions with SEO keywords woven into answers.
 *
 * SEO / GEO / AEO approach:
 * - Target keywords: "recurring gifting", "one-stop shop", "schedule greeting cards",
 *   "automated birthday cards", "Auto Send", e-cards / mailed cards / flowers
 * - Keywords live in answers, section copy, and meta content — not in question text.
 * - Section headings contain relevant long-tail keywords naturally.
 * - Each page has its own meta title + description for unique search snippets.
 *
 * Business rules:
 * - Users manually schedule each message (pick date, write content).
 * - Messages are sent automatically on the scheduled date.
 * - Users receive a notification before each message goes out.
 * - Avoid pricing anchors (no "$" or plan pricing language).
 * - Frame "early access" as a benefit with a safer grandfathering promise.
 */

import { FaqQuestions } from '../constants/faqQuestions';
import { brandColors } from '../config/theme';
import { HOW_IT_WORKS_STEPS } from './howItWorksSteps';
import {
  faqAutoSendAnswer,
  faqContactImportAnswer,
  faqFlowerPricingAnswer,
  faqFreePlanAnswerFallback,
  faqMailedCardPricingAnswerFallback,
  faqMemberPricingAnswerFallback,
  faqPage,
  faqWhoCanOrderAnswer,
} from './faqContent';
import {
  AUTO_SEND_DEFINITION,
  AUTO_SEND_LANDING_LEAD,
  AUTO_SEND_PUBLIC_NAME,
  PUBLIC_PAGE_META,
} from './publicPageMeta';

export {
  faqAutoSendAnswer,
  faqAutoSendPlanAnswer,
  faqFlowerPricingAnswer,
  faqFreePlanAnswerFallback,
  faqMailedCardPricingAnswerFallback,
  faqMemberPricingAnswerFallback,
  faqWhoCanOrderAnswer,
} from './faqContent';

const faqFreePlanAnswer = faqFreePlanAnswerFallback;
const faqMailedCardPricingAnswer = faqMailedCardPricingAnswerFallback;
const faqMemberPricingAnswer = faqMemberPricingAnswerFallback;

export const landingContent = {
  brand: {
    name: 'Scheduled Smiles',

    /** Emotional hook — kept for potential reuse, no longer displayed in hero */
    hook: 'Never miss a moment that matters only because life got busy.',

    /** Brand tagline — used as the final phrase in the hero rotator (in amber) */
    tagline: 'Schedule a smile for everyone you love.',

    /**
     * Hero headline template — the occasion word rotates with a smooth animation.
     * The full H1 in the DOM is the static version for SEO crawlers.
     */
    headlineTemplate: 'Never forget another',
    headlineH1: 'Never forget another birthday, anniversary, or milestone.',

    /** Rotating occasion words for the hero headline. Each has its own accent color. */
    headlineRotatorWords: [
      { text: 'birthday.', color: brandColors.indigoText },
      { text: 'anniversary.', color: brandColors.tealText },
      { text: 'milestone.', color: brandColors.amberText },
    ],

    /**
     * Hero positioning line — category differentiator gets first read after the H1 (PBI #1238).
     * "One-stop shop" must stay prominent for SEO/GEO and for scanners.
     */
    heroBenefit: 'Your one-stop shop for recurring gifting.',

    /**
     * Hero support line — product mix + autopilot outcome under the positioning claim.
     */
    heroCategory:
      'Free e-cards, mailed greeting cards, and flowers \u2014 scheduled in one place, on autopilot.',

    /**
     * @deprecated Prefer heroBenefit + heroCategory. Kept so older references
     * and crawl parity can still join the full hero story in one string.
     */
    subheadline:
      'Your one-stop shop for recurring gifting. Free e-cards, mailed greeting cards, and flowers \u2014 scheduled in one place, on autopilot.',

    /**
     * Scannable differentiator chips under the category line (not a paragraph).
     * icon keys map in HeroCopyStack / HeroHighlightIconKey.
     * "AI assistant" covers writing, artwork, and product help — not messages only.
     */
    heroHighlights: [
      { label: 'Auto Send', icon: 'autoSend' as const },
      { label: 'AI assistant', icon: 'ai' as const },
      { label: 'You stay in control', icon: 'control' as const },
    ],

    /**
     * Answer-first Auto Send definition for crawl HTML / FAQ parity (GEO).
     * Not rendered in the landing hero — chips name Auto Send instead.
     */
    autoSendLead: AUTO_SEND_LANDING_LEAD,

    /**
     * @deprecated Use headlineRotatorWords instead. Kept temporarily for TextRotator
     * compatibility until the component is updated.
     */
    descriptionPhrases: [
      { text: 'Set it up once.', color: brandColors.indigoText },
      { text: 'We\u2019ll remind you.', color: brandColors.heartViolet },
      { text: 'Deliver on the day.', color: brandColors.amberText },
    ],
  },

  /** Hero pill badge — trust signal above the headline */
  heroPill: 'Peace of Mind Guaranteed',

  /**
   * Occasion names allowed in the hero headline rotator (case-insensitive match
   * against public event type names). Keeps the rotator focused on high-intent
   * holidays instead of every catalog occasion.
   */
  HEADLINE_ROTATOR_ALLOWLIST: [
    'birthday',
    'anniversary',
    "valentine's day",
    "mother's day",
    "father's day",
    'christmas',
    'independence day',
    'halloween',
  ] as const,

  cta: {
    /** Primary CTA — clean and direct */
    primary: 'Start for Free',

    /** Secondary CTA — for returning users */
    secondary: 'Sign in',

    /**
     * Primary action for onboarded / signed-in users on marketing pages and the
     * public nav account menu. Navigates into the authenticated Overview home.
     * `goToApp` is kept as an alias for PublicNavAccountMenu (same label).
     */
    goToOverview: 'Go to Overview',
    goToApp: 'Go to Overview',

    /** Badge shown next to the primary CTA — Free = no per-card charge for e-cards */
    badge: 'Includes Free E-Cards',

    /** Grandfathering promise — displayed in the Final CTA section */
    microcopy: 'Free plan available \u2014 early users keep everything they set up.',

    /**
     * Fallback price-anchor microcopy when subscription plans have not loaded.
     * Prefer the dynamic "Personal plan from $X/mo" string when plans are available.
     */
    priceAnchorFallback: 'Personal plan available \u2014 see Pricing',
  },

  /** Roadmap pill — used in the Coming Soon section (gift cards ship with mailed cards when offered) */
  roadmapPill: 'Text Message Greetings \u00B7 Business CRM \u2014 coming soon',

  /** Trust strip — compact credibility bullets */
  trust: [
    'Set up in under 5 minutes',
    'Pre-send reminder so you can review',
    'Always in control \u2014 edit or cancel before delivery',
    'Contacts don\u2019t need an account',
    'E-cards, mailed greeting cards, and flowers \u2014 scheduled in one place',
    'Privacy-first. No spam.',
  ],

  /**
   * Per-page SEO meta tags (title, description, canonical path).
   * Single source of truth is `publicPageMeta.ts` (PBI #1270) so runtime, build-time crawl
   * HTML, and index.html cannot drift. Each page passes its entry to `usePageMeta`.
   */
  meta: {
    landing: PUBLIC_PAGE_META.home,
    howItWorks: PUBLIC_PAGE_META.howItWorks,
    guidesIndex: PUBLIC_PAGE_META.guidesIndex,
    automatedOccasionGiftsGuide: PUBLIC_PAGE_META.automatedOccasionGiftsGuide,
    faq: PUBLIC_PAGE_META.faq,
    cards: PUBLIC_PAGE_META.cards,
    flowers: PUBLIC_PAGE_META.flowers,
    mailedCards: PUBLIC_PAGE_META.mailedCards,
    gifts: PUBLIC_PAGE_META.gifts,
    pricing: PUBLIC_PAGE_META.pricing,
    emailDeliveryHelp: PUBLIC_PAGE_META.emailDeliveryHelp,
    terms: PUBLIC_PAGE_META.terms,
    privacy: PUBLIC_PAGE_META.privacy,
    aiHub: PUBLIC_PAGE_META.aiHub,
    aiUseCaseCatalog: PUBLIC_PAGE_META.aiUseCaseCatalog,
    aiUseCaseScheduling: PUBLIC_PAGE_META.aiUseCaseScheduling,
  },

  sections: {
    howItWorks: {
      title: 'How it works',
      steps: [
        {
          title: '1. Tell us who matters',
          description:
            'Add a birthday, anniversary, or any date you don\u2019t want to forget. Start with just one \u2014 it takes 2 minutes.',
        },
        {
          title: '2. Choose your gift and personalize it',
          description:
            'Pick a free e-card, a mailed card, flowers, or combine them. Add a personal message and set the delivery date.',
        },
        {
          title: '3. Auto Send delivers on time',
          description:
            'Turn on Auto Send and your e-card, mailed card, or flowers arrive on the scheduled date. You get a reminder beforehand so you can review, tweak, or just let it go.',
        },
      ],
    },

    /**
     * Expanded How It Works page content — richer than the landing page version.
     * Used on the dedicated /how-it-works page.
     */
    howItWorksPage: {
      heroTitle: 'How Scheduled Smiles Works',
      heroSubtitle: 'Schedule once. We handle the rest. You get the credit.',

      /** Emotional problem statement — frames the pain before showing the solution */
      problemTitle: 'Life gets busy. Dates slip by.',
      problemBody:
        'Between work, family, and everything in between, it\u2019s easy to let birthdays and anniversaries slip past. You care deeply \u2014 you just need a system that remembers for you.',

      /**
       * Expanded step descriptions for the full page (longer than landing page).
       * Also feeds the HowTo JSON-LD and the /how-it-works crawl snippet.
       */
      expandedSteps: HOW_IT_WORKS_STEPS,

      /** Feature highlights — selling points not covered in the 3 steps */
      features: [
        {
          icon: 'bell',
          title: 'Pre-send reminders',
          description:
            'Get notified before every gift goes out. Review, edit, or let it send as-is \u2014 you\u2019re always in control.',
        },
        {
          icon: 'repeat',
          title: 'Recurring events',
          description:
            'Set a birthday or anniversary once. E-cards send and flowers or mailed cards can be re-scheduled every year automatically \u2014 no calendar reminders needed.',
        },
        {
          icon: 'userOff',
          title: 'No account needed for recipients',
          description:
            'Your contacts receive a beautiful e-card by email, a mailed card in the post, or flowers at the door. Nothing to install, nothing to sign up for.',
        },
        {
          icon: 'shield',
          title: 'Privacy-first',
          description:
            'Your contacts\u2019 data is never shared, sold, or used for marketing. You control everything.',
        },
        {
          icon: 'flower',
          title: 'Flower delivery',
          description:
            'Schedule real flower arrangements from local florists, timed to your occasion. Pair with an e-card or mailed card for the ultimate gift.',
        },
        {
          icon: 'mailedCard',
          title: 'Mailed cards',
          description:
            'Schedule a physical greeting card to arrive on the day. Personalize the message panels and we handle printing and postage.',
        },
      ],
    },

    /**
     * Differentiation section — answers "why not just a calendar reminder?"
     * Rendered after How It Works and before the Trust Strip.
     */
    differentiation: {
      title: 'Why not just a calendar reminder?',
      points: [
        {
          title: 'Reminders still need you to act',
          description:
            'A birthday reminder app or calendar ping only tells you the date is coming. You still have to find a card, write a message, and send it on a busy day.',
        },
        {
          title: 'We deliver on the day',
          description:
            'Schedule a free e-card, mailed card, or flowers once \u2014 we deliver on the occasion so thoughtfulness does not depend on your free time.',
        },
        {
          title: 'Set once and relax',
          description:
            'Get a pre-send reminder, stay in control, and enjoy the peace of mind that the gift is already handled.',
        },
      ],
    },

    /** Explore section — three-product heading */
    exploreCards: {
      title: 'Schedule a smile \u2014 free e-cards, mailed greeting cards, and flowers',
      /** Shown when no system templates exist */
      emptyState: 'We\u2019re adding new e-cards every week. Sign up to be the first to try them.',
    },

    /** Customise section — feature highlights (e-card media; accurate, not forced onto mailed cards) */
    customise: {
      title: 'Make it completely yours',
      features: [
        {
          icon: 'photo',
          title: 'Your photo or video',
          description: 'Swap in any image or clip to make an e-card personal.',
        },
        {
          icon: 'pencil',
          title: 'Your words',
          description: 'Write from scratch or edit an e-card \u2014 it\u2019s your message.',
        },
        {
          icon: 'calendar',
          title: 'Your timing',
          description: 'Pick exactly when an e-card lands in their inbox.',
        },
      ],
    },

    /** Social proof — aspirational scenario vignettes (no fake attributions) */
    socialProof: {
      title: 'What if you never forgot again?',
      scenarios: [
        'What if your grandmother thought you were the most thoughtful grandchild\u2026 and you never had to remember?',
        'Imagine: a bouquet, a mailed card, or a free e-card arrives on the day \u2014 timed perfectly, with zero last-minute panic.',
        'No more \u201Chappy belated\u201D texts. Just \u201Cthank you, that made my day.\u201D',
      ],
    },

    /** Coming soon section — unified roadmap teaser with single email capture */
    comingSoon: {
      title: 'Even more on the way',
      subtitle: 'We\u2019re just getting started. Here\u2019s what\u2019s next for Scheduled Smiles:',
      features: [
        'Text Message Greetings \u2014 reach anyone, anywhere, instantly via SMS.',
        'Business Subscription with CRM Integration \u2014 sync clients from your CRM and automate thoughtful greetings at scale.',
      ],
      ctaLabel: 'Keep me in the loop',
      ctaPlaceholder: 'your@email.com',
      successMessage: 'You\u2019re on the list \u2014 we\u2019ll let you know when new features drop.',
    },

    /**
     * Occasion-reminder lead magnet — remind yourself about someone else's occasion
     * without creating an account yet (PBI #1066).
     */
    occasionReminder: {
      title: 'Do future-you a favor',
      subtitle:
        'Remind yourself about someone else\u2019s birthday, anniversary, or milestone \u2014 free, once a year, seven days before.',
      personLabel: 'Their first name',
      personPlaceholder: 'Alex',
      occasionLabel: 'Occasion',
      occasionPlaceholder: 'Select an occasion',
      monthLabel: 'Month',
      dayLabel: 'Day',
      emailLabel: 'Your email',
      emailPlaceholder: 'you@example.com',
      submitLabel: 'Remind me',
      successMessage: 'Check your email to confirm the reminder',
    },

    finalCta: {
      title: "Let\u2019s make forgetting a thing of the past.",
      subtitle: 'Set it up once. We handle delivery. You get the credit.',
    },

    footer: {
      tagline: 'Privacy-first. No spam.',
      /** Intentionally empty — early-stage product note removed (PBI #1064). */
      note: '',
      copyright: `\u00A9 ${new Date().getFullYear()} Scheduled Smiles. All rights reserved.`,
      links: [
        { label: 'Terms and Conditions', path: '/terms' },
        { label: 'Privacy Policy', path: '/privacy' },
      ],
    },
  },

  /** E-Cards page — heading and intro for the dedicated /cards gallery */
  cardsPage: {
    heroTitle: 'Browse E-Cards',
    heroSubtitle:
      'Choose from free, professionally designed e-cards for every occasion. Personalize with your photos and message, then schedule delivery.',
  },

  /** Flowers page — heading and intro for the dedicated /flowers gallery */
  flowersPage: {
    heroTitle: 'Browse Flower Arrangements',
    heroSubtitle:
      'Explore fresh arrangements from local florists for every occasion. Schedule delivery for exactly the right day.',
  },
  mailedCardsPage: {
    heroTitle: 'Browse Mailed Cards',
    heroSubtitle:
      'Choose a physical card design, personalize each writable panel, and schedule delivery ahead of the occasion.',
  },
  giftsPage: {
    heroTitle: 'Browse Gifts',
    heroSubtitle:
      'Curated physical gifts ship directly to your contact. Schedule ahead of the occasion; you are charged once when we send the gift to Goody.',
  },

  /**
   * Intent guide — /guides/automated-occasion-gifts (GEO pillar page).
   */
  automatedOccasionGiftsGuide: {
    heroTitle: 'Automatically send birthday flowers and cards so you never forget',
    heroSubtitle: AUTO_SEND_DEFINITION,
    sections: [
      {
        title: 'Auto Send vs a calendar reminder',
        body:
          'A birthday reminder app or calendar alert only tells you a date is coming. You still have to find a gift and send it on a busy day. Auto Send schedules the e-card, mailed card, or flower delivery in advance and sends birthday cards and gifts automatically on the occasion — with a pre-send reminder so you stay in control.',
      },
      {
        title: 'When to send an e-card vs a mailed card vs flowers',
        bullets: [
          {
            label: 'E-cards',
            text: 'Instant email delivery; free static templates; best for quick, personal messages.',
          },
          {
            label: 'Mailed cards',
            text: 'Physical cards posted to their door; schedule ahead for printing and postage.',
          },
          {
            label: 'Flowers',
            text: 'Local florist delivery timed to the occasion; schedule at least a week ahead when possible.',
          },
        ],
      },
    ],
    ctaTitle: 'Set it up once. Auto Send handles the rest.',
    ctaSubtitle: 'Free plan available — upgrade to Personal for hands-free delivery on every date you care about.',
  },

  /**
   * FAQ page — expanded with category groupings (source: faqContent.ts).
   * Used on the dedicated /faq page and the /faq crawl snippet.
   */
  faqPage,

  /** Preview modal CTA copy (cards) */
  previewModal: {
    cta: 'Use this card',
    helperText: 'Create your free account to schedule it',
    helperTextAuthenticated: 'Pick a contact and schedule delivery',
  },

  /** Flower preview modal CTA copy */
  flowerPreviewModal: {
    cta: 'Use These Flowers',
    helperText: 'Free to browse. Sign up to schedule delivery.',
    helperTextAuthenticated: 'Pick a contact and schedule delivery',
  },

  /** Mailed-card preview modal CTA copy */
  mailedCardPreviewModal: {
    cta: 'Use This Card',
    helperText: 'Free to browse. Sign up to schedule delivery.',
    helperTextAuthenticated: 'Pick a contact and schedule delivery',
  },

  /** Gift preview modal CTA copy (Explore + public gifts shop) */
  giftPreviewModal: {
    cta: 'Use This Gift',
    helperText: 'Free to browse. Sign up to schedule delivery.',
    helperTextAuthenticated: 'Pick a contact and schedule delivery',
  },

  /**
   * Public mailed-card PDP format trust (PBI #1203) — badge + strip explain physical delivery.
   */
  mailedCardPdp: {
    formatBadge: 'Mailed in an envelope',
    formatTrustStrip: [
      { title: 'Printed card', subtitle: 'Your design inside' },
      { title: 'Sealed envelope', subtitle: 'We mail it for you' },
      { title: 'Postage included', subtitle: 'No shipping fee' },
    ],
  },

  /**
   * Public flower PDP fulfillment trust (PBI #1234) — icon strip for Shopping landing pages.
   */
  flowerPdp: {
    fulfillmentTrustStrip: [
      {
        title: 'Local florist delivery',
        subtitle: 'Hand-arranged near your recipient — supports local florists',
      },
      {
        title: 'Hand-arranged',
        subtitle: 'Fresh flowers, professionally styled',
      },
    ],
    substitutionNote:
      'Local florists may substitute flowers of equal or greater value when needed so your arrangement still arrives on time.',
  },

  /**
   * Gift-card add-on copy (PBI #990) — show only when config reports offered amounts.
   */
  mailedCardGiftCard: {
    shopSubline: 'Optional digital gift cards can be added when you schedule — face value plus a small fulfillment fee shown at checkout.',
    /** Short retail-style label for the floating landing carousel callout. */
    carouselBadge: 'Gift cards available',
    previewHelper: 'You can add an optional digital gift card during scheduling; a small fulfillment fee applies when you include one.',
    faqGiftCardCost:
      'Gift cards are optional digital add-ons on mailed-card orders. You pay the card price plus the gift-card face value you choose and a small gift card fulfillment fee; all amounts are shown before you confirm.',
  },

  /**
   * Flat FAQ list — used for the mini-FAQ on the landing page and JSON-LD schema.
   * The first 3 items are the conversion-critical questions shown on the landing page.
   */
  faq: [
    {
      q: `What is ${AUTO_SEND_PUBLIC_NAME}?`,
      a: faqAutoSendAnswer,
    },
    {
      q: FaqQuestions.IsReallyFree,
      a: faqFreePlanAnswer,
    },
    {
      q: 'Will my friend know it was automated?',
      a: 'No. They receive a beautiful, personalized e-card from you via email, a mailed card in the post, or flowers at the door. There\u2019s no mention of scheduling or automation \u2014 it simply looks like you remembered and took the time to send something thoughtful. Because you did.',
    },
    {
      q: 'What if I want to change my message after scheduling?',
      a: 'You can edit or cancel a scheduled e-card at any time before it\u2019s sent. You\u2019ll also get a pre-send reminder a few days before delivery, so you always have a chance to review and tweak your message. Mailed-card and flower orders can be changed while they are still pending, before they are placed with the vendor. Mailed cards for dates close to today may be placed with our mailing partner right away when you submit (or when Cardly\'s estimate requires it), and then they cannot be cancelled in the app.',
    },
    {
      q: FaqQuestions.WhoCanOrder,
      a: faqWhoCanOrderAnswer,
    },
    {
      q: FaqQuestions.WhatIsMemberPricing,
      a: faqMemberPricingAnswer,
    },
    {
      q: 'What happens if I forget to review before it sends?',
      a: 'We send you a notification before delivery so you can review, edit, or let it go as-is. If you don\u2019t respond to the reminder, the gift goes out with what you already approved when you scheduled it.',
    },
    {
      q: 'Can I add my own photos or videos?',
      a: 'Yes for e-cards. Upload your own images or videos to personalize a free e-card with photos that matter. Professionally designed e-cards with built-in media are also available if you prefer something ready-made.',
    },
    {
      q: 'Do my contacts need to sign up?',
      a: 'No. Recipients do not need an account. E-cards arrive by email, mailed cards arrive in the post, and flowers are delivered to the door \u2014 nothing to install or sign up for.',
    },
    {
      q: FaqQuestions.CanIImportContacts,
      a: faqContactImportAnswer,
    },
    {
      q: 'How far ahead can I schedule?',
      a: 'As far ahead as you like for e-cards. For flower delivery, we recommend scheduling at least 7 days ahead. For mailed cards, schedule early enough for printing and postage \u2014 the order flow shows estimated arrival windows.',
    },
    {
      q: 'What if flowers can\u2019t be delivered on the scheduled date?',
      a: 'If your chosen delivery date is unavailable (for example, Sundays or holidays), we\u2019ll automatically try to deliver up to 3 days earlier \u2014 choosing the closest available date before the event. You\u2019ll be notified by email if the date is adjusted. If no dates are available within that window, the order won\u2019t be placed and you won\u2019t be charged.',
    },
    {
      q: FaqQuestions.SubscriptionCoversFlowers,
      a: faqFlowerPricingAnswer,
    },
    {
      q: 'How do mailed cards work?',
      a: 'Mail a greeting card online: browse physical card designs, personalize the writable panels, and schedule the arrival date. We print and post the card through our mailing partner so it arrives on time.',
    },
    {
      q: FaqQuestions.SubscriptionCoversMailedCards,
      a: faqMailedCardPricingAnswer,
    },
    {
      q: 'Can I add a gift card to a mailed card?',
      a: 'When gift cards are available, you can add an optional digital gift card during scheduling. You pay the card price plus the gift-card face value you choose and a small gift card fulfillment fee; all amounts are shown before you confirm.',
    },
  ],
} as const;
